import { useState } from "react";
import { useSevenAccess, SevenShell } from "./DashboardSeven";
import { NEWLYWEDS, ROUTINE, ACCESS } from "../../data/sevenNights";
import { Check, Moon, ArrowRight, Info } from "lucide-react";
import { Navigate, useLocation } from "react-router-dom";
import { getStored, setStored, FONT_DISPLAY } from "../../lib/sevenTheme";

const ProgressBar = ({ done, total }) => (
  <div className="flex items-center gap-3 mb-7">
    <div className="text-[11.5px] text-[#A7956E] tracking-[0.18em] font-bold">التقدّم</div>
    <div className="flex-1 h-1.5 bg-[#1C1010] rounded-full overflow-hidden">
      <div className="h-full bg-gradient-to-r from-[#8A6E3D] to-[#E2C281] transition-all" style={{ width: `${(done / total) * 100}%` }} />
    </div>
    <div className="text-[12px] text-[#C9A66A] font-bold tabular-nums">{done}/{total}</div>
  </div>
);

const NightCard = ({ night, completed, onToggle, open, onOpen }) => (
  <div className={`rounded-2xl transition-all ${completed ? "bg-[#1A0F0F]/80 border border-[#C9A66A]/35" : "bg-gradient-to-b from-[#1C1010] to-[#150C0C] border border-[#C9A66A]/20 hover:border-[#C9A66A]/45"}`}>
    <button
      onClick={onOpen}
      data-testid={`seven-night-${night.n}-toggle`}
      className="w-full text-right px-6 pt-5 pb-4 flex items-center gap-4"
    >
      <span className="w-11 h-11 rounded-full bg-gradient-to-br from-[#C9A66A] to-[#8A6E3D] flex items-center justify-center font-extrabold text-[#0A0606] text-[16px] shrink-0">{night.n}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.24em] text-[#A7956E] font-bold mb-0.5">ليلة {night.n}</div>
        <div className="font-bold text-[16px] text-white" style={{ fontFamily: FONT_DISPLAY }}>{night.title}</div>
      </div>
      {completed && <Check className="w-5 h-5 text-[#C9A66A] shrink-0" strokeWidth={3} />}
    </button>
    {open && (
      <div className="px-6 pb-6 space-y-4 border-t border-[#C9A66A]/12 pt-4 mt-2 text-[14px] leading-[1.95]" data-testid={`seven-night-${night.n}-body`}>
        {night.note && (
          <div className="bg-[#2A1414]/60 border border-[#C9A66A]/25 rounded-lg px-4 py-3 text-[13px] text-[#E7DDC0] flex items-start gap-2.5">
            <Info className="w-4 h-4 text-[#C9A66A] mt-0.5 shrink-0" />
            <span>{night.note}</span>
          </div>
        )}
        <div><span className="text-[11px] tracking-[0.22em] text-[#C9A66A] font-bold">الهدف</span><div className="text-[#E7DDC0] mt-1">{night.goal}</div></div>
        {night.setup && <div><span className="text-[11px] tracking-[0.22em] text-[#C9A66A] font-bold">التحضير</span><div className="text-[#E7DDC0] mt-1">{night.setup}</div></div>}
        {night.startLine && <div className="text-[14px] text-[#F5EAD3] italic">{night.startLine}</div>}
        <div>
          <div className="text-[11px] tracking-[0.22em] text-[#C9A66A] font-bold mb-2">الأسئلة</div>
          <ol className="space-y-2 text-[#E7DDC0]">
            {night.questions.map((q, i) => (
              <li key={i} className="flex gap-2.5"><span className="text-[#C9A66A] tabular-nums shrink-0">{i + 1}.</span><span>{q}</span></li>
            ))}
          </ol>
        </div>
        {night.activity && (
          <div><span className="text-[11px] tracking-[0.22em] text-[#C9A66A] font-bold">النشاط</span><div className="text-[#E7DDC0] mt-1">{night.activity}</div></div>
        )}
        {night.examples && night.examples.length > 0 && (
          <div>
            <div className="text-[11px] tracking-[0.22em] text-[#C9A66A] font-bold mb-2">أمثلة</div>
            <ul className="grid sm:grid-cols-2 gap-1.5">
              {night.examples.map((e, i) => <li key={i} className="text-[13.5px] text-[#E7DDC0] flex items-start gap-2"><span className="text-[#C9A66A]">·</span>{e}</li>)}
            </ul>
          </div>
        )}
        {night.challenge && (
          <div><span className="text-[11px] tracking-[0.22em] text-[#C9A66A] font-bold">تحدّي لطيف</span><div className="text-[#E7DDC0] mt-1">{night.challenge}</div></div>
        )}
        {night.closing && (
          <div className="text-[14px] text-[#F5EAD3] italic border-t border-[#C9A66A]/12 pt-3 mt-3">{night.closing}</div>
        )}
        <button
          onClick={onToggle}
          data-testid={`seven-night-${night.n}-complete`}
          className={`mt-4 w-full inline-flex items-center justify-center gap-2 font-extrabold text-[13.5px] tracking-wide rounded-full px-6 py-3 min-h-[48px] transition-all ${
            completed
              ? "bg-[#1A0F0F] border border-[#C9A66A]/40 text-[#C9A66A]"
              : "bg-gradient-to-b from-[#E2C281] to-[#C9A66A] hover:from-[#EBCD93] hover:to-[#D6B477] text-[#0A0606]"
          }`}
        >
          {completed ? <><Check className="w-4 h-4" /> تم إنهاء هذه الليلة</> : <>أنهيت هذه الليلة <ArrowRight className="w-4 h-4 rotate-180" /></>}
        </button>
      </div>
    )}
  </div>
);

export default function ProgramSeven() {
  const location = useLocation();
  const which = location.pathname.endsWith("routine-reset") ? "routine-reset" : "newlyweds";
  const { loading, bundle } = useSevenAccess();
  const [openNight, setOpenNight] = useState(1);
  const [completed, setCompleted] = useState(() => getStored().completed || {});

  if (loading) return <div className="min-h-screen bg-[#0A0606] flex items-center justify-center text-[#A7956E]">جاري التحميل…</div>;

  const program = which === "routine-reset" ? ROUTINE : NEWLYWEDS;
  const access = ACCESS[bundle] || ACCESS.starter;
  const allowed = which === "routine-reset" ? access.routine : access.newlyweds;

  if (!allowed) return <Navigate to="/7days/dashboard" replace />;

  const progKey = which === "routine-reset" ? "routine" : "newlyweds";
  const progDone = Object.entries(completed[progKey] || {}).filter(([, v]) => v).length;

  const toggle = (n) => {
    const cur = { ...(completed[progKey] || {}) };
    cur[n] = !cur[n];
    const next = { ...completed, [progKey]: cur };
    setCompleted(next);
    setStored({ completed: next });
  };

  return (
    <SevenShell title={program.title} testid={`seven-program-${progKey}-root`}>
      <p className="text-[14.5px] text-[#CFC3A1] leading-[2] mb-6 max-w-2xl">{program.intro}</p>

      <div className="bg-[#1C1010]/50 border border-[#C9A66A]/18 rounded-xl p-5 mb-8" data-testid="seven-program-rules">
        <div className="text-[11px] tracking-[0.22em] text-[#C9A66A] font-bold mb-3 flex items-center gap-2"><Moon className="w-3.5 h-3.5" /> قواعد بسيطة</div>
        <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-2 text-[13px] text-[#E7DDC0]">
          {program.rules.map((r, i) => (
            <li key={i} className="flex items-start gap-2"><span className="text-[#C9A66A] mt-1">·</span><span>{r}</span></li>
          ))}
        </ul>
      </div>

      <ProgressBar done={progDone} total={program.nights.length} />

      <div className="space-y-3">
        {program.nights.map((night) => (
          <NightCard
            key={night.n}
            night={night}
            completed={!!(completed[progKey] || {})[night.n]}
            onToggle={() => toggle(night.n)}
            open={openNight === night.n}
            onOpen={() => setOpenNight(openNight === night.n ? -1 : night.n)}
          />
        ))}
      </div>
    </SevenShell>
  );
}
